import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, HostListener } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { TreeNode } from 'tree';
import { Project, Scenario } from '@model';
import {
  CookieService,
  LoaderService,
  ProjectService,
  RouterService,
  ScenarioService,
  SidenavService,
  TreeService,
} from '@service';

@Component({
  selector: 'optima-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
})
export class TreeComponent {
  loaded = false;
  areProjects = false;
  selectedNode: TreeNode<never>;
  selectedNodes = new Set<TreeNode<never>>();
  expandedNodes = new Set<string>();
  projects: Project[] = [];
  scenarios: Scenario[] = [];
  treeData: TreeNode<never>[] = [];
  treeHeight = '0px';
  filterValue = '';
  dataSource = new MatTreeNestedDataSource<TreeNode<never>>();
  searchProperties: string[] = ['label', 'id'];

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.calculateHeight();
  }

  constructor(
    private router: Router,
    private projectService: ProjectService,
    private sidenavService: SidenavService,
    private scenarioService: ScenarioService,
    private cookieService: CookieService,
    private routerService: RouterService,
    private loaderService: LoaderService,
    private treeService: TreeService,
  ) {
    this.projectService.projectsChange$.subscribe(() => {
      this.buildTree();
      this.areProjects = !!this.projectService.projects.length;
      if (!this.areProjects) {
        this.loaded = true;
      }
    });
    this.scenarioService.scenariosChange$.subscribe(loaded => {
      this.buildTree();
      this.loaded = this.loaded || !!loaded;
      if (this.loaded) {
        this.treeService.enableFilterInput();
      }
    });
    this.projectService.searchFilter$.subscribe(value => {
      this.filterValue = value;
    });
    this.treeService.autoSelectInTree$.subscribe(() => {
      this.autoSelectNodeInTree();
    });
  }

  buildTree(): void {
    this.filterValue = '';
    this.treeData = this.projectService.projects.map(project => {
      const filteredScenarios = this.scenarioService.scenarios.filter(
        scenario => scenario.parentId === project.id,
      );
      const scenariosForTree: TreeNode<never>[] = filteredScenarios.map(s => {
        const scenarioTreeNode: TreeNode<never> = {
          id: s.id,
          label: s.displayLabel ? s.displayLabel : 'None',
          type: 'SCENARIO',
          parentId: project.id,
        };
        return scenarioTreeNode;
      });
      const projectForTree: TreeNode<never> = {
        id: project.id,
        label: project.displayLabel ? project.displayLabel : 'None',
        children: scenariosForTree,
        type: 'PROJECT',
      };
      return projectForTree;
    });
    this.dataSource.data = this.treeData;
    if (!this.selectedNode) {
      this.autoSelectNodeInTree();
    }
    try {
      this.calculateHeight();
    } catch (e) {}
    window.dispatchEvent(new Event('resize'));
  }

  createProject(): void {
    this.loaderService.showLoader('global');
    void this.sidenavService.closeIfMobile();

    const locale = this.cookieService.getLocale();
    this.projectService
      .createProject$({
        displayLabel: '',
        locale,
      })
      .pipe(
        switchMap(project => {
          const newProjectId = project.id;
          project.displayLabel = `Untitled Project ${newProjectId}`;
          return this.projectService.updateProject$(newProjectId, project);
        }),
      )
      .subscribe(project => {
        const flatTree = this.flatTree(this.treeData);
        const projectNode = flatTree.find(
          node => node['type'] === 'PROJECT' && node.id === project.id,
        );
        this.onProjectClicked(projectNode, true);
      });
  }

  onProjectClicked(
    project: TreeNode<never>,
    autoSelect: boolean = false,
  ): void {
    this.loaderService.showLoader('global');
    void this.sidenavService.closeIfMobile();
    if (!this.routerService.isNavigating) {
      this.router
        .navigateByUrl(`/details/${project.id}/view-project`)
        .then(() => {
          this.selectedNode = project;
          if (autoSelect) {
            this.treeService.autoSelectInTree();
          }
        });
    }
  }

  onScenarioClicked(scenario: TreeNode<never>): void {
    this.loaderService.showLoader('global');
    void this.sidenavService.closeIfMobile();
    this.router
      .navigate([`/details/${scenario.id}/view-scenario`], {
        queryParams: { projectId: scenario['parentId'] },
      })
      .then(() => {
        this.selectedNode = scenario;
      });
  }

  calculateHeight(): void {
    const globalNavHeight = document.querySelector('global-nav').clientHeight;
    const sidenavHeaderHeight =
      document.querySelector('.sidenav-header').clientHeight;
    const createButtonHeight = document.querySelector(
      '#create-button-container',
    ).clientHeight;
    this.treeHeight = `${
      window.innerHeight -
      globalNavHeight -
      sidenavHeaderHeight -
      createButtonHeight
    }px`;
  }

  clicked(node: TreeNode<never>): void {
    if (node['type'] === 'SCENARIO') {
      this.onScenarioClicked(node);
    } else {
      this.onProjectClicked(node);
    }
  }

  linked(node: TreeNode<never>): void {
    if (node['type'] === 'SCENARIO') {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/details/${node.id}/view-scenario`], {
          queryParams: { projectId: node['parentId'] },
        }),
      );
      window.open(url, '_blank');
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/details/${node.id}/view-project`]),
      );
      window.open(url, '_blank');
    }
  }

  private flatTree(tree: TreeNode<never>[]): TreeNode<never>[] {
    const result = [];

    for (let i = 0; i < tree.length; i++) {
      const node = tree[i];
      result.push(node);
      if (node.children) {
        result.push(...node.children);
      }
    }

    return result;
  }

  private autoSelectNodeInTree(): void {
    if (
      window.location.href.includes('view-scenario') ||
      window.location.href.includes('create-scenario') ||
      window.location.href.includes('edit-scenario')
    ) {
      let scenarioId: string;
      if (window.location.href.includes('create-scenario')) {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        scenarioId = params.get('scenarioId');
      } else {
        scenarioId = this.scenarioService.getScenarioIdFromUrl();
      }
      this.selectedNodes = new Set([
        this.findNodeByIdAndType(this.treeData, scenarioId, 'SCENARIO'),
      ]);
    }
    if (window.location.href.includes('view-project')) {
      const projectId = this.getProjectIdFromUrl(window.location.href);
      this.selectedNodes = new Set([
        this.findNodeByIdAndType(this.treeData, projectId, 'PROJECT'),
      ]);
    }
  }

  private getProjectIdFromUrl(url: string): string {
    const regex = /\/(\d+)\/view-project/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  private findNodeByIdAndType(
    nodeList: TreeNode<never>[],
    id: string,
    type: string,
  ): TreeNode<never> | null {
    for (const node of nodeList) {
      if (node.id === id && node['type'] === type) {
        return node;
      }
      if (node.children) {
        const foundNode = this.findNodeByIdAndType(node.children, id, type);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }
}
